/**
 * @fileoverview Zod schema for the unified roster's "Edit User" form.
 * @description Runs before the update-sales-user request is sent. Same field set and conditional
 * `reportsTo` requirement the old add-user form had — editing a user's role can change
 * whether a reporting manager is required at all (promoting someone to Sales Head clears it; moving
 * them off Sales Head requires picking one).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { z } from 'zod'

import { emailField, mobileNumberField } from '@/shared/lib/validation/contact-fields.schema'

export const editSalesUserFormSchema = z
  .object({
    name: z.string().trim().min(1, 'sales.userManagement.editUserForm.errors.nameRequired'),
    email: emailField({
      required: 'sales.userManagement.editUserForm.errors.emailRequired',
      invalid: 'sales.userManagement.editUserForm.errors.emailInvalid',
    }),
    phone: mobileNumberField(),
    role: z.enum(['Sales Head', 'Sales Manager', 'Sales Executive'], {
      message: 'sales.userManagement.editUserForm.errors.roleRequired',
    }),
    reportsTo: z.string().trim().optional(),
  })
  .refine((data) => data.role === 'Sales Head' || Boolean(data.reportsTo), {
    message: 'sales.userManagement.editUserForm.errors.reportsToRequired',
    path: ['reportsTo'],
  })

export type EditSalesUserFormValues = z.infer<typeof editSalesUserFormSchema>
